// src/context/AuthContext.tsx
import { createContext, useState, useEffect, useContext } from 'react';
import type { ReactNode } from 'react';
import { getMe, logout as apiLogout } from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { clearAuthToken, isElectron } from '../lib/authToken';

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  user: any | null;
  refreshSession: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any | null>(null);
  const navigate = useNavigate();

  const refreshSession = async () => {
    setLoading(true);
    try {
      const res = await getMe();
      setIsAuthenticated(true);
      setUser(res);
    } catch (err) {
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    if (isElectron()) clearAuthToken();
    try {
      await apiLogout();
    } catch (e) {
      // Optionally handle error
    }
    await refreshSession();
    navigate('/login');
  };

  useEffect(() => {
    refreshSession();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, loading, user, refreshSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
};